import { useMemo } from "react";
import { Space } from "antd";
import dayjs from "dayjs";
import { isNil } from "lodash";
import PropTypes from "prop-types";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import { API_DATE_FORMAT } from "utils/constants";
import {
  DATE_BOUND_MODES,
  DATE_BOUND_UNIT,
  DATE_BOUND_UNITS,
  FIELD_VALIDATION_LIMITS,
  normalizeDateBound,
} from "utils/validation-rules";

const UI_BOUND_MODES = {
  NONE: "none",
  ABSOLUTE: "absolute",
  DURATION: "duration",
};

export const parseBound = (raw) => {
  const bound = normalizeDateBound(raw);
  if (!bound) {
    return {
      mode: UI_BOUND_MODES.NONE,
      absolute: null,
      duration: null,
    };
  }
  if (bound.mode === DATE_BOUND_MODES.DATE) {
    return {
      mode: UI_BOUND_MODES.ABSOLUTE,
      absolute: bound.date,
      duration: null,
    };
  }
  return {
    mode: UI_BOUND_MODES.DURATION,
    absolute: null,
    duration: bound,
  };
};

export const parseDurationParts = (
  duration,
  defaultSign = "-",
  defaultUnit = DATE_BOUND_UNIT.YEAR,
) => {
  if (!duration) return { amount: 0, unit: defaultUnit, sign: defaultSign };
  const amount = Number(duration.amount) || 0;
  let sign = defaultSign;
  if (amount < 0) sign = "-";
  if (amount > 0) sign = "+";
  return {
    sign,
    amount: Math.abs(amount),
    unit: duration.unit || defaultUnit,
  };
};

export const buildDateBound = (date) => ({
  mode: DATE_BOUND_MODES.DATE,
  date,
});

export const buildDurationBound = (sign, amount, unit) => {
  const absolute = Math.abs(Number(amount) || 0);
  return {
    mode: DATE_BOUND_MODES.DURATION,
    amount: sign === "-" ? -absolute : absolute,
    unit,
  };
};

export const NumberField = ({ label, value, onChange, placeholder, step }) => (
  <Input
    type="number"
    label={label}
    placeholder={placeholder}
    step={step}
    value={isNil(value) ? "" : value}
    onChange={(event) => {
      const raw = event.target.value;
      if (raw === "") {
        onChange(undefined);
        return;
      }
      const asNumber = Number(raw);
      onChange(Number.isNaN(asNumber) ? raw : asNumber);
    }}
  />
);

NumberField.propTypes = {
  label: PropTypes.string,
  value: PropTypes.any,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  step: PropTypes.any,
};

NumberField.defaultProps = {
  label: undefined,
  value: undefined,
  onChange: undefined,
  placeholder: undefined,
  step: undefined,
};

export const DateBoundEditor = ({
  label,
  value,
  labels = {},
  onChange,
  allowNone = true,
  defaultSign = "-",
  defaultAmount = FIELD_VALIDATION_LIMITS.ADULT_MIN_AGE,
  defaultUnit = DATE_BOUND_UNIT.YEAR,
}) => {
  const bound = useMemo(() => parseBound(value), [value]);
  const durationParts = useMemo(
    () => parseDurationParts(bound.duration, defaultSign, defaultUnit),
    [bound.duration, defaultSign, defaultUnit],
  );

  const modeOptions = [
    ...(allowNone
      ? [{ value: UI_BOUND_MODES.NONE, label: labels.BOUND_NONE }]
      : []),
    {
      value: UI_BOUND_MODES.ABSOLUTE,
      label: labels.BOUND_ABSOLUTE,
    },
    {
      value: UI_BOUND_MODES.DURATION,
      label: labels.BOUND_DURATION,
    },
  ];

  return (
    <Space direction="vertical" size={8} className="canvas-editor__full-width">
      <Select
        label={label}
        options={modeOptions}
        value={
          bound.mode === UI_BOUND_MODES.NONE && !allowNone
            ? undefined
            : bound.mode
        }
        onChange={(mode) => {
          if (mode === UI_BOUND_MODES.NONE) onChange(undefined);
          else if (mode === UI_BOUND_MODES.ABSOLUTE) {
            onChange(buildDateBound(dayjs().format(API_DATE_FORMAT)));
          } else {
            onChange(
              buildDurationBound(defaultSign, defaultAmount, defaultUnit),
            );
          }
        }}
      />
      {bound.mode === UI_BOUND_MODES.ABSOLUTE ? (
        <Input
          type="date"
          variant="simple"
          label={labels.BOUND_DATE}
          value={
            bound.absolute ? dayjs(bound.absolute, API_DATE_FORMAT) : undefined
          }
          onChange={(date) =>
            onChange(
              date ? buildDateBound(date.format(API_DATE_FORMAT)) : undefined,
            )
          }
        />
      ) : null}
      {bound.mode === UI_BOUND_MODES.DURATION ? (
        <>
          <NumberField
            label={labels.DURATION_AMOUNT}
            value={durationParts.amount}
            placeholder={String(defaultAmount)}
            onChange={(amount) =>
              onChange(
                buildDurationBound(
                  durationParts.sign,
                  amount,
                  durationParts.unit,
                ),
              )
            }
          />
          <Select
            label={labels.DURATION_UNIT}
            options={[
              { value: DATE_BOUND_UNIT.DAY, label: labels.UNIT_DAYS },
              { value: DATE_BOUND_UNIT.MONTH, label: labels.UNIT_MONTHS },
              { value: DATE_BOUND_UNIT.YEAR, label: labels.UNIT_YEARS },
            ]}
            value={durationParts.unit}
            onChange={(unit) =>
              onChange(
                buildDurationBound(
                  durationParts.sign,
                  durationParts.amount,
                  unit,
                ),
              )
            }
          />
          <Select
            label={labels.DURATION_DIRECTION}
            options={[
              {
                value: "-",
                label: labels.DURATION_BEFORE,
              },
              {
                value: "+",
                label: labels.DURATION_AFTER,
              },
            ]}
            value={durationParts.sign}
            onChange={(sign) =>
              onChange(
                buildDurationBound(
                  sign,
                  durationParts.amount,
                  durationParts.unit,
                ),
              )
            }
          />
        </>
      ) : null}
    </Space>
  );
};

DateBoundEditor.propTypes = {
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  labels: PropTypes.object,
  onChange: PropTypes.func,
  allowNone: PropTypes.bool,
  defaultSign: PropTypes.string,
  defaultAmount: PropTypes.number,
  defaultUnit: PropTypes.oneOf(DATE_BOUND_UNITS),
};

DateBoundEditor.defaultProps = {
  label: undefined,
  value: undefined,
  labels: {},
  onChange: undefined,
  allowNone: true,
  defaultSign: "-",
  defaultAmount: FIELD_VALIDATION_LIMITS.ADULT_MIN_AGE,
  defaultUnit: DATE_BOUND_UNIT.YEAR,
};
